import { Direction } from '@angular/cdk/bidi';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  HostListener,
  inject,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IgxOverlayOutletDirective } from '@infragistics/igniteui-angular';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { AuthenticationService, ConfigurationService } from 'app/shared';
import { NeedsToUnsubscribe } from 'app/shared/core';
import { registerDynamicLocale } from 'app/shared/core/register-locale';
import { LoadUserInfo } from 'app/state/actions/user-info.actions';
import { AppState } from 'app/state/app.state';
import { ShowTranslationKeyService } from 'app/transloco/custom-fallback-strategy';
import { of } from 'rxjs';
import { filter, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { ThemeName, ThemeService } from './library/shared/theme-service';
import { ApplicationInsightService } from './shared/errors/application-insights.service';
import { HubSpotService } from './shared/services/hubspot.service';
import { IgxGlobalOverlayService } from './shared/services/igx-global-overlay.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent extends NeedsToUnsubscribe implements OnInit {
  private static readonly SavedCompanyContextKey = 'currentCompanyIdAndUserIdAndProductContext';
  router = inject(Router);
  private configService = inject(ConfigurationService);
  private translate = inject(TranslocoService);
  private authService = inject(AuthenticationService);
  private store = inject<Store<AppState>>(Store);
  private renderer = inject(Renderer2);
  private igxGlobalOverlay = inject(IgxGlobalOverlayService);
  private showTranslationKeyService = inject(ShowTranslationKeyService);
  private _ = inject(HubSpotService);
  private _document = inject<Document>(DOCUMENT);

  private currentUserLanguage: string;
  private userInfo$ = this.store.select((state) => state.userInfo);
  private keysPressed = [];
  buildInfo = '';
  buildInfoVisible = false;
  loadingCurrent = false;
  currentThemeIdx = 0;
  dir: Direction = 'ltr';

  @HostListener('document:keydown', ['$event']) handleKeydown(event: KeyboardEvent) {
    this.keysPressed.push(event.key);
    this.keysPressed = this.keysPressed.slice(-3);
    switch (this.keysPressed.join(';')) {
      case 'Escape;Escape;Escape':
        this.toggleBuildinfo();
        break;
      case 'Escape;Escape;1':
        this.toggleTheme('petrol');
        break;
      case 'Escape;Escape;2':
        this.toggleTheme('lavender');
        break;
      case 'Escape;Escape;0':
        this.resetTheme();
        break;
      case 'Escape;Escape;t':
        this.showTranslationKeyService.toggleTranslationKeys();
        break;
      default:
        break;
    }
  }

  @ViewChild(IgxOverlayOutletDirective) set overlay(value: IgxOverlayOutletDirective) {
    this.igxGlobalOverlay.register(value);
  }
  themeService = inject(ThemeService);
  constructor() {
    const applicationInsights = inject(ApplicationInsightService);

    super();
    applicationInsights.trackPageView();
  }
  ngOnInit(): void {
    this.translate.setDefaultLang('en');

    this.authService.userLoadededEvent.pipe(takeUntil(this.unsubscribe$)).subscribe((_) => {
      if (this.authService.loggedIn && this.currentUserLanguage == null && !this.loadingCurrent) {
        this.loadingCurrent = true;
        this.store.dispatch(new LoadUserInfo(this.getSavedCompanyId()));
      }
    });

    this.userInfo$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((u) => u !== null),
      )
      .subscribe((user) => {
        if (user.userId != null) {
          this.currentUserLanguage = user.languageCode;
          registerDynamicLocale(user.displayLanguageCode);
          this.setI18NAttributes(user.languageCode, user.rtl ? 'rtl' : 'ltr');
          if (user.status < 1 || !user.companyActive) {
            this.authService.startSignoutMainWindow();
          }
          this.loadingCurrent = false;
        }
      });

    this.store
      .select((s) => s.surveyState)
      .subscribe((surveyState) => {
        this.setI18NAttributes(surveyState.languageCode, surveyState.languageDir);
      });

    this.hideStartupSplashWhenReady();
  }

  private hideStartupSplashWhenReady(): void {
    // Auth transit routes leave the app (or route onwards) right away, so keep the splash up through them.
    const isAuthTransitUrl = (url: string) =>
      /^\/(auth-redirect|signin-callback|signout-callback)(?:[/?#]|$)/.test(url);
    const navigationEndUrls$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    );
    const navigatedUrls$ = this.router.navigated
      ? navigationEndUrls$.pipe(startWith(this.router.url))
      : navigationEndUrls$;

    navigatedUrls$
      .pipe(
        filter((url) => !isAuthTransitUrl(url)),
        take(1),
        switchMap(() =>
          this.authService.loggedIn
            ? this.userInfo$.pipe(
                filter((user) => user.userId !== null),
                take(1),
              )
            : of(null),
        ),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => this.startSplashExit());
  }

  private startSplashExit(): void {
    const splash = this._document.getElementById('app-startup-splash');
    const appRoot = this._document.querySelector('app-root');
    const window = this._document.defaultView;

    if (!splash || !window) {
      if (appRoot) {
        this.renderer.removeAttribute(appRoot, 'aria-hidden');
      }
      return;
    }

    let exitStarted = false;
    const beginExit = () => {
      if (exitStarted) return;

      exitStarted = true;
      let removalTimeout = 0;
      const removeSplash = () => {
        window.clearTimeout(removalTimeout);
        splash.remove();
      };
      const removeAfterAnimation = (event: AnimationEvent) => {
        if (event.target === splash) {
          splash.removeEventListener('animationend', removeAfterAnimation);
          removeSplash();
        }
      };

      if (appRoot) {
        this.renderer.removeAttribute(appRoot, 'aria-hidden');
      }
      this.renderer.setAttribute(splash, 'aria-hidden', 'true');
      splash.addEventListener('animationend', removeAfterAnimation);
      this.renderer.addClass(splash, 'startup-splash--exit');
      removalTimeout = window.setTimeout(removeSplash, 800);
    };

    window.requestAnimationFrame(() => window.requestAnimationFrame(beginExit));
    window.setTimeout(beginExit, 200);
  }

  private setI18NAttributes(lang: string, dir: Direction) {
    this.renderer.setAttribute(this._document.documentElement, 'lang', lang);
    this.dir = dir;
    this.renderer.setAttribute(this._document.documentElement, 'dir', this.dir);
  }
  toggleTheme(theme: ThemeName) {
    this.themeService.setTheme(theme);
  }

  resetTheme() {
    this.themeService.resetToStoreTheme();
  }

  private getSavedCompanyId(): number | undefined {
    const savedContext = localStorage.getItem(AppComponent.SavedCompanyContextKey);
    if (!savedContext) return undefined;

    try {
      const parsed = JSON.parse(savedContext) as { companyId?: number };
      return parsed?.companyId > 0 ? parsed.companyId : undefined;
    } catch {
      return undefined;
    }
  }

  toggleBuildinfo() {
    const config = this.configService.configuration;
    this.buildInfo =
      config.BuildName === '!Local'
        ? 'Local build'
        : `
                ${config.BuildName}, Build: ${config.BuildVersion}
                 source branch [${config.BuildBranch}]
                `;
    this.buildInfoVisible = !this.buildInfoVisible;
    this.keysPressed = [];
  }
}
